/* Replace legacy useSendVerificationCode hook with this one after the refactor */

import { SignInIdentifier } from '@logto/schemas';
import { conditional } from '@silverhand/essentials';
import { HTTPError, TimeoutError } from 'ky';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CaptchaContext from '@/Providers/CaptchaContextProvider/CaptchaContext';
import UserInteractionContext from '@/Providers/UserInteractionContextProvider/UserInteractionContext';
import { sendVerificationCodeApi } from '@/apis/utils';
import useApi from '@/hooks/use-api';
import useErrorHandler, { type ErrorHandlers } from '@/hooks/use-error-handler';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import useToast from '@/hooks/use-toast';
import {
  UserFlow,
  type ContinueFlowInteractionEvent,
  type VerificationCodeIdentifier,
} from '@/types';
import { codeVerificationTypeMap } from '@/utils/sign-in-experience';

type Payload = {
  identifier: VerificationCodeIdentifier;
  value: string;
};

const useSendVerificationCode = (flow: UserFlow, replaceCurrentPage?: boolean) => {
  const [errorMessage, setErrorMessage] = useState<string>();
  const navigate = useNavigateWithPreservedSearchParams();
  const { executeCaptcha } = useContext(CaptchaContext);

  const handleError = useErrorHandler();
  const { setToast } = useToast();
  const { t } = useTranslation();
  const asyncSendVerificationCode = useApi(sendVerificationCodeApi);
  const { setVerificationId, getVerificationContextRevision } = useContext(UserInteractionContext);

  const active = useRef(true);
  const pending = useRef(false);
  const generation = useRef(0);
  useEffect(() => {
    active.current = true;
    generation.current += 1;
    return () => {
      active.current = false;
      generation.current += 1;
    };
  }, [flow]);

  const clearErrorMessage = useCallback(() => {
    setErrorMessage('');
  }, []);

  const onSubmit = useCallback(
    async (
      { identifier, value }: Payload,
      interactionEvent?: ContinueFlowInteractionEvent,
      errorHandlers?: ErrorHandlers
    ) => {
      if (pending.current || !active.current) {
        return;
      }
      pending.current = true;
      const revision = getVerificationContextRevision();
      const requestGeneration = generation.current;
      const current = () =>
        active.current &&
        requestGeneration === generation.current &&
        revision === getVerificationContextRevision();
      try {
        const captchaToken = await executeCaptcha();
        if (!current()) {
          return;
        }

        const [error, result] = await asyncSendVerificationCode(
          flow,
          {
            type: identifier,
            value,
          },
          interactionEvent,
          captchaToken
        );

        if (!current()) {
          return;
        }
        if (error) {
          if (!(error instanceof HTTPError) && !(error instanceof TimeoutError)) {
            setErrorMessage(t('error.send_verification_code_failed'));
            return;
          }
          await handleError(error, {
            'guard.invalid_input': () => {
              setErrorMessage(
                t(
                  identifier === SignInIdentifier.Email
                    ? 'error.invalid_email'
                    : 'error.invalid_phone'
                )
              );
            },
            'session.email_blocklist.email_not_allowed': () => {
              setErrorMessage(t('error.send_verification_code_failed'));
            },
            'session.email_blocklist.email_subaddressing_not_allowed': () => {
              setErrorMessage(t('error.send_verification_code_failed'));
            },
            // The hosted email service usage cap has been reached. Show a friendly, generic "couldn't
            // send the code" toast instead of the raw API "usage limit" message.
            'connector.usage_limit_exceeded': () => {
              setToast(t('error.send_verification_code_failed'));
            },
            global: () => {
              setErrorMessage(t('error.send_verification_code_failed'));
            },
            // Per-call overrides win over the defaults above, so a caller can react to a specific
            // failure differently (e.g. sign-in falls back to the password page on a cap hit).
            ...errorHandlers,
          });

          return;
        }

        if (result) {
          // Store the verification ID in the context so that we can use it in the next step
          setVerificationId(codeVerificationTypeMap[identifier], result.verificationId);

          navigate(
            {
              pathname: `/${flow}/verification-code`,
              search: window.location.search,
            },
            {
              replace: replaceCurrentPage,
              // Append the interaction event to the state so that we can use it in the next step
              ...conditional(
                flow === UserFlow.Continue && {
                  state: { interactionEvent },
                }
              ),
            }
          );
        }
      } finally {
        pending.current = false;
      }
    },
    [
      getVerificationContextRevision,
      asyncSendVerificationCode,
      flow,
      handleError,
      navigate,
      replaceCurrentPage,
      setVerificationId,
      executeCaptcha,
      setToast,
      t,
    ]
  );

  return {
    errorMessage,
    clearErrorMessage,
    onSubmit,
  };
};

export default useSendVerificationCode;
